import { A } from "@solidjs/router";
import { For, JSX } from "solid-js";
import { CategoryData } from "~/server/types/category";
import { getCategorySlugFromCategoryName } from "~/utils/common";
import { categoryIconMap } from "~/assets/category_icons";
import { CaretRight } from "~/assets/svg_icons/svg_icons";

export type CategoryCardV2Props = {
  categories: CategoryData[];
  onBrowseClick?: () => void;
};

/**
 * CategoryCardV2 Component
 * A 3x3 grid card layout for categories on mobile (non-sticky state)
 * Based on Figma design: https://www.figma.com/design/uTQhl6K0EfVzl9uxl4Y4o1/Demand-master?node-id=884-16888
 */
export function CategoryCardV2(props: CategoryCardV2Props): JSX.Element {
  const BrowseIcon = categoryIconMap.BROWSE;

  // Take first 9 categories for the 3x3 grid
  const displayCategories = () => props.categories.slice(0, 9);

  return (
    <div class="box-border flex w-full flex-col gap-[10px] rounded-[16px] bg-background-mapping-reverse p-[6px]">
      <div class="overflow-clip rounded-[16px] border border-solid border-stroke-1 bg-background-normal-primary shadow-[0px_2px_11px_0px_rgba(0,0,0,0.05)]">
        {/* 3x3 Category Grid */}
        <div class="grid grid-cols-3">
          <For each={displayCategories()}>
            {(category, index) => {
              const IconComponent = categoryIconMap[category.name];
              const row = Math.floor(index() / 3);
              const col = index() % 3;

              // Determine border classes based on position
              const borderClasses = [
                // Right border for non-last columns
                col < 2 ? "border-r" : "",
                // Bottom border for non-last rows
                row < 2 ? "border-b" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <A
                  href={`/shop-by-category/${getCategorySlugFromCategoryName(category.name)}`}
                  class={`flex flex-col items-center gap-[6px] border-stroke-1 px-3 py-4 ${borderClasses}`}
                >
                  {/* Icon - same pattern as strip */}
                  <div class="relative size-[24px] shrink-0 stroke-text-normal-secondary">
                    {IconComponent ? (
                      <IconComponent class="size-[24px]" />
                    ) : (
                      <img
                        src={category.metadata.iconUrl}
                        alt={category.metadata.title}
                        class="block size-[24px] max-w-none"
                      />
                    )}
                  </div>

                  {/* Label */}
                  <div class="w-full overflow-hidden overflow-ellipsis whitespace-nowrap text-center text-label-medium text-text-normal-secondary">
                    {category.metadata.title}
                  </div>
                </A>
              );
            }}
          </For>
        </div>

        {/* Browse All Categories Footer */}
        <div class="border-t border-stroke-1">
          <button
            type="button"
            onClick={props.onBrowseClick}
            class="flex w-full cursor-pointer items-center justify-center gap-[6px] bg-transparent px-0 pb-4 pt-4"
          >
            <div class="size-[24px] stroke-text-normal-secondary">
              <BrowseIcon class="size-[24px]" />
            </div>
            <div class="flex items-center justify-center">
              <span class="text-label-medium text-text-normal-secondary">
                Browse all categories
              </span>
              <CaretRight
                class="text-text-normal-secondary"
                width={20}
                height={20}
              />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
